/**
 * The count beside "My work" in the rail — bata-ui#166 W3-4, `docs/adr/9101`.
 *
 * How many alert groups (DEC-101, ADR 9040) — or, on a platform that does not count groups, alerts —
 * assigned to the signed-in person are in the queue today, from
 * `GET /me/work-counts`, re-read every minute. When any assigned alert is past due the chip
 * carries a warning glyph and says so in words: the chip is white with the figure in ink, so no
 * colour is asked to carry a meaning (design/09 §12.6). No badge at all when nothing waits, when
 * the person may not read alerts, or when the read has not answered — never a zero chip, and
 * never a number the platform did not state.
 */

import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { QueryClientContext } from '@tanstack/react-query';

import { formatNumber } from '@/lib/format';

import { useWorkCounts, workBadge } from './work-counts';

/**
 * The rail renders the badge only under a query client. The chrome is also drawn on its own —
 * its tests, the sign-in surface — and a missing count there is no count, not a crash.
 */
export function WorkBadge({ compact = false }: { readonly compact?: boolean }): React.JSX.Element | null {
  return useContext(QueryClientContext) === undefined ? null : <CountedBadge compact={compact} />;
}

function CountedBadge({ compact }: { readonly compact: boolean }): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const badge = workBadge(useWorkCounts().data);
  if (badge.kind === 'none') return null;
  const late = badge.overdue > 0;
  return (
    <span
      data-work-badge={late ? 'overdue' : 'waiting'}
      // Brief part 1 §3: a white chip, the figure in ink, mono 600 — one of the rail's only two
      // counters. `rail-bg` is the ink here in both themes, because the rail does not invert.
      className={[
        'bg-rail-fg text-rail-bg font-numeric inline-flex shrink-0 items-center gap-1 py-[1px] text-[11px] font-semibold leading-4',
        compact ? 'px-1' : 'px-[6px]',
      ].join(' ')}
    >
      {late && <AlertTriangle size={12} aria-hidden />}
      <span aria-hidden className="tabular-nums">{formatNumber(badge.count, i18n.language)}</span>
      <span className="sr-only">
        {t(badge.groups === true
          ? (late ? 'myWork.badge.groupsLabelOverdue' : 'myWork.badge.groupsLabel')
          : (late ? 'myWork.badge.labelOverdue' : 'myWork.badge.label'), { count: badge.count, overdue: badge.overdue })}
      </span>
    </span>
  );
}
